import {CloseOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Card, Col, Row, Space} from 'antd';
import {
  AccountExtraFee,
  AccountExtraFeeRegion,
  AccountExtraFeeType,
  AccountFieldsFragment,
  AccountInvoicePaymentMethod,
  BillingPlans
} from 'api/generated';
import {AnchorLink, Hint, IBANInput} from 'components';
import {FieldArray, useFormikContext} from 'formik';
import {AutoComplete, FormItem, Input, InputNumber, Select, Switch} from 'formik-antd';
import {usePaymentMethods} from 'hooks/usePaymentMethods';
import {POSSIBLE_FEES} from 'lib/enums';
import {formatPercent} from 'lib/formaters';
import React from 'react';
import {accountInvoicesVatOptions, checkIfRegionAll, InputAddon} from './utils';
import {BillingFormValues} from './types';
import {
  amountInputProps,
  bigAmountInputProps,
  checkIfRegional,
  isFeeWithPaymentMethods
} from './utils';

const {Option} = Select;

const DEFAULT_STEP_FEES = {
  from: 0,
  trxFlatFee: 24,
  trxFee: 0.9,
  bizumTrxFlatFee: 34,
  bizumTrxFee: 1.34
};

export const BillingDetailsForm: React.FC<{
  account: AccountFieldsFragment;
  formProps: any;
}> = ({account, formProps}) => {
  const {values, setFieldValue} = useFormikContext<BillingFormValues>();
  const [paymentMethods, loading] = usePaymentMethods(account.id);

  // Utility function to determine input props based on fee type and region
  const getInputNumberProps = (extraFee: AccountExtraFee | undefined, index: number) => {
    if (extraFee?.type && checkIfRegional(extraFee.type)) {
      return amountInputProps;
    }

    if (extraFee?.region && checkIfRegionAll(extraFee.region)) {
      return {
        ...(extraFee?.type === AccountExtraFeeType.PERCENT ? undefined : amountInputProps),
        style: {width: 140},
        addonAfter: (
          <Select name={`fees.extra.${index}.type`} style={{width: 70}}>
            <Select.Option value="percent">%</Select.Option>
            <Select.Option value="money">EUR</Select.Option>
          </Select>
        )
      };
    } else {
      return {
        addonAfter: <InputAddon>%</InputAddon>
      };
    }
  };

  return (
    <>
      <FormItem name="dynamicBilling" label="Dynamic billing">
        <Switch name="dynamicBilling" />
      </FormItem>
      {values.dynamicBilling && (
        <>
          <FormItem name="billingPlan" label="Billing plan">
            <Select
              name="billingPlan"
              options={Object.values(BillingPlans).map((text) => ({
                value: text,
                label: text.replace('_', ' ')
              }))}
            />
          </FormItem>
          <FormItem name="iban" label="IBAN">
            <IBANInput name="iban" countryCode={account.business?.country || 'ES'} />
          </FormItem>
          <FormItem name="notifications.emailOnAccountInvoice" label="Email admin on invoice">
            <Switch name="notifications.emailOnAccountInvoice" />
          </FormItem>
          <FormItem name="billingEmail" label="Billing email">
            <Input name="billingEmail" type="email" placeholder={account.adminEmail!} />
          </FormItem>
          <FormItem name="accountInvoicePaymentMethod" label="Payment method">
            <Select name="accountInvoicePaymentMethod">
              <Option key={1} value={AccountInvoicePaymentMethod.BANK_TRANSFER}>
                Bank transfer
              </Option>
              <Option key={2} value={AccountInvoicePaymentMethod.DIRECT_DEBIT}>
                Direct debit
              </Option>
            </Select>
          </FormItem>
          <FormItem
            name="invoiceNotesString"
            label={
              <Space>
                <span>Custom invoice notes</span>
                <Hint message="These notes will be added to the notes section of the PDF invoice" />
              </Space>
            }>
            <Input.TextArea name="invoiceNotesString" />
          </FormItem>
          <FormItem
            name="groupInvoicesByStore"
            label={
              <Space>
                <span>Group invoice info by store</span>
                <Hint message="If enabled, invoice line items will be grouped by store" />
              </Space>
            }>
            <Switch name="groupInvoicesByStore" />
          </FormItem>
          <FormItem name="billingVatPercent" label="Account billing VAT">
            <Select name="billingVatPercent">
              {Object.keys(accountInvoicesVatOptions).map((vatKey) => (
                <Option key={accountInvoicesVatOptions[vatKey]}>
                  {vatKey} ({formatPercent(accountInvoicesVatOptions[vatKey])})
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem name="customizePlan" label="Customize plan">
            <Switch name="customizePlan" />
          </FormItem>
          {values.customizePlan && (
            <>
              <FormItem name="fees.dailyPlatformFee" label="Daily platform usage fee">
                <InputNumber name="fees.dailyPlatformFee" {...amountInputProps} />
              </FormItem>
              <FormItem name="fees.dailyExtraSeatsFee" label="Daily extra seat fee in EUR">
                <InputNumber name="fees.dailyExtraSeatsFee" {...amountInputProps} />
              </FormItem>
              <FormItem
                name="fees.weeklyCustomDomainFee"
                label={
                  <Space>
                    <span>Weekly custom domain fee in EUR</span>
                    <Hint message="This fee is applied for owning a custom domain. If the account has no custom domain, the fee is not applied." />
                  </Space>
                }>
                <InputNumber name="fees.weeklyCustomDomainFee" {...amountInputProps} />
              </FormItem>
            </>
          )}
          <FormItem name="fees.highRiskFee" label="High-risk transaction surcharge">
            <InputNumber name="fees.highRiskFee" addonAfter={<InputAddon>%</InputAddon>} />
          </FormItem>
          <FieldArray name="fees.extra">
            {({remove, push}) => (
              <>
                {values.fees?.extra?.map((extraFee: AccountExtraFee, index) => (
                  <FormItem
                    name={`fees.extra.${index}.name`}
                    key={'fees.extra.' + index}
                    wrapperCol={{span: 24}}>
                    <Row gutter={2}>
                      <Col span={formProps.labelCol?.span}>
                        <AutoComplete
                          name={`fees.extra.${index}.name`}
                          filterOption
                          placeholder="Fee name"
                          options={POSSIBLE_FEES.map((text) => ({
                            value: text,
                            label: text
                          }))}
                        />
                      </Col>
                      <Col>
                        <InputNumber
                          name={`fees.extra.${index}.amount`}
                          placeholder="Amount"
                          style={{width: 120}}
                          {...getInputNumberProps(extraFee, index)}
                        />
                      </Col>
                      <Col span={7}>
                        <Select
                          name={`fees.extra.${index}.${
                            checkIfRegional(extraFee?.type) ? 'type' : 'region'
                          }`}
                          onChange={(value) => {
                            if (checkIfRegional(value)) {
                              setFieldValue(`fees.extra.${index}.type`, value);
                            } else {
                              setFieldValue(
                                `fees.extra.${index}.type`,
                                AccountExtraFeeType.PERCENT
                              );
                            }
                          }}>
                          <Select.Option value={AccountExtraFeeType.DAILY}>per day</Select.Option>
                          <Select.Option value={AccountExtraFeeType.MONTHLY}>
                            per month
                          </Select.Option>
                          <Select.Option value={AccountExtraFeeType.ONE_TIME}>
                            one time
                          </Select.Option>
                          <Select.Option value={AccountExtraFeeRegion.DOMESTIC}>
                            of successful EU credit cards transactions
                          </Select.Option>
                          <Select.Option value={AccountExtraFeeRegion.NON_DOMESTIC}>
                            of successful <b>non</b> EU credit cards transactions
                          </Select.Option>
                          <Select.Option value={AccountExtraFeeRegion.ALL}>
                            of successful transactions
                          </Select.Option>
                        </Select>
                      </Col>
                      {isFeeWithPaymentMethods(values.fees.extra?.[index]) && (
                        <Col>
                          <Select
                            mode="multiple"
                            placeholder="Payment methods"
                            name={`fees.extra.${index}.paymentMethods`}
                            style={{width: 170}}
                            loading={loading}>
                            {paymentMethods.map((pm) => (
                              <Select.Option value={pm} key={pm}>
                                {pm}
                              </Select.Option>
                            ))}
                          </Select>
                        </Col>
                      )}
                      <Col>
                        <Select name={`fees.extra.${index}.masterAccountFee`} style={{width: 170}}>
                          <Select.Option value={true}>Master account fee</Select.Option>
                          <Select.Option value={false}>MONEI fee</Select.Option>
                        </Select>
                      </Col>
                      <Col span={2}>
                        <Button icon onClick={() => remove(index)}>
                          <CloseOutlined />
                        </Button>
                      </Col>
                    </Row>
                  </FormItem>
                ))}
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="add">
                  <Button
                    onClick={() =>
                      push({
                        name: '',
                        amount: 0,
                        type: AccountExtraFeeType.PERCENT,
                        region: AccountExtraFeeRegion.ALL
                      })
                    }
                    type="dashed"
                    icon={<PlusOutlined />}>
                    Add extra fee
                  </Button>
                </FormItem>
              </>
            )}
          </FieldArray>
          <FormItem name="customizeSteps" label="Customize billing steps">
            <Switch name="customizeSteps" />
          </FormItem>
          {values.customizeSteps && (
            <FieldArray name="fees.steps">
              {({remove, push}) => (
                <>
                  {values.fees?.steps?.map((step, index) => (
                    <Card
                      type="inner"
                      size="small"
                      key={'fees.steps.' + index}
                      style={{marginBottom: 10}}
                      title={`Step #${index + 1}`}
                      extra={
                        <AnchorLink onClick={() => remove(index)}>
                          <DeleteOutlined /> Remove step
                        </AnchorLink>
                      }>
                      <FormItem
                        name={`fees.steps.${index}.from`}
                        label="From monthly volume in EUR"
                        style={{marginRight: 10}}>
                        <InputNumber
                          name={`fees.steps.${index}.from`}
                          style={{width: 200}}
                          step={100000}
                          {...bigAmountInputProps}
                        />
                      </FormItem>
                      <FormItem name={`fees.steps.${index}.trxFee`} label="Card transaction fee">
                        <InputNumber name={`fees.steps.${index}.trxFee`} addonAfter="%" />
                      </FormItem>
                      <FormItem
                        name={`fees.steps.${index}.bizumTrxFee`}
                        label="Bizum transaction fee">
                        <InputNumber name={`fees.steps.${index}.bizumTrxFee`} addonAfter="%" />
                      </FormItem>
                      <FormItem
                        name={`fees.steps.${index}.trxFlatFee`}
                        label="Flat fee per card transaction">
                        <InputNumber
                          name={`fees.steps.${index}.trxFlatFee`}
                          {...amountInputProps}
                        />
                      </FormItem>
                      <FormItem
                        name={`fees.steps.${index}.bizumTrxFlatFee`}
                        label="Flat fee per bizum transaction">
                        <InputNumber
                          name={`fees.steps.${index}.bizumTrxFlatFee`}
                          {...amountInputProps}
                        />
                      </FormItem>
                    </Card>
                  ))}
                  <FormItem
                    wrapperCol={{
                      offset: formProps.labelCol?.span,
                      ...formProps.wrapperCol
                    }}
                    name="add">
                    <Button
                      style={{marginTop: 20}}
                      onClick={() => push(DEFAULT_STEP_FEES)}
                      type="dashed"
                      icon={<PlusOutlined />}>
                      Add new billing step
                    </Button>
                  </FormItem>
                </>
              )}
            </FieldArray>
          )}
        </>
      )}
    </>
  );
};
